/* eslint-disable react/jsx-props-no-spreading */
import { AudienceType, MatchingCriteria } from '@/client/services/api/graphql/gql/graphql';
import {
  Box,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { FaMinus, FaPlus } from 'react-icons/fa6';
import { useEffect, useState } from 'react';

import { AssignmentFormData } from '../../../types/assignmentTypes';
import { useTranslation } from 'react-i18next';

interface AssignmentAudienceHiredDateFormProps {
  isInitialLoad: boolean;
}

export default function AssignmentAudienceHiredDateForm({
  isInitialLoad,
}: AssignmentAudienceHiredDateFormProps) {
  const { t } = useTranslation();

  const { watch, setValue, control } = useFormContext<AssignmentFormData>();
  const { audience, useHiredDateAudienceFilter } = watch();

  const hasHiredDateFilter = audience.some((item) => item.type === AudienceType.HiredDate);

  const hiredDateFilter = audience.find((item) => item.type === AudienceType.HiredDate);

  const [days, setDays] = useState(hiredDateFilter?.days ?? 0);

  // Handle initial state
  useEffect(() => {
    if (hasHiredDateFilter && !useHiredDateAudienceFilter) {
      setValue('useHiredDateAudienceFilter', true);
    }
  }, []);

  const handleSwitchChange = (checked: boolean) => {
    if (checked) {
      if (!hasHiredDateFilter) {
        setValue('audience', [
          ...audience,
          {
            type: AudienceType.HiredDate,
            days,
            matchingCriteria: MatchingCriteria.MatchAll,
            name: t('admin.assignments.audience.additionalFilters.hiredWithinDays'),
            segmentationType: 'HIRED_DATE',
          },
        ]);
      }
    } else {
      setValue(
        'audience',
        audience.filter((item) => item.type !== AudienceType.HiredDate),
      );
    }
    setValue('useHiredDateAudienceFilter', checked);
  };

  const handleDaysChange = (value: number) => {
    setDays(value);
    if (hasHiredDateFilter) {
      setValue(
        'audience',
        audience.map((item) =>
          item.type === AudienceType.HiredDate
            ? {
                ...item,
                days: value,
              }
            : item,
        ),
      );
    }
  };

  return (
    <Stack>
      <Flex alignItems="center" justifyContent="space-between">
        <Stack spacing={0.5}>
          <Text variant="createLabel">
            {t('admin.assignments.audience.additionalFilters.hiredWithinDays', {
              days,
            })}
          </Text>
          <Text variant="createHelpText">
            {t('admin.assignments.audience.additionalFilters.hiredWithinDays.helpText')}
          </Text>
        </Stack>
        <Controller
          name="useHiredDateAudienceFilter"
          control={control}
          render={({ field: { value, ...field } }) => (
            <Switch
              {...field}
              variant="create"
              isChecked={value}
              onChange={(e) => handleSwitchChange(e.target.checked)}
            />
          )}
        />
      </Flex>

      <Collapse in={useHiredDateAudienceFilter}>
        <Flex justifyContent="space-between" alignItems="center" paddingX={4}>
          <HStack>
            <IconButton
              size="sm"
              variant="createNumberStepper"
              isRound
              icon={<FaMinus />}
              aria-label={t('admin.assignments.audience.additionalFilters.decreaseDays')}
              onClick={() => handleDaysChange(Math.max(0, days - 1))}
            />

            <Box width={16}>
              <Input
                type="number"
                value={days}
                variant="createNumber"
                fontSize="14px"
                fontWeight={600}
                min={0}
                onChange={({ target: { value } }) => {
                  if (value === '') {
                    handleDaysChange(0);
                    return;
                  }
                  const numValue = parseInt(value, 10);
                  if (!Number.isNaN(numValue) && numValue >= 0) {
                    handleDaysChange(numValue);
                  }
                }}
              />
            </Box>

            <IconButton
              size="sm"
              variant="createNumberStepper"
              isRound
              icon={<FaPlus />}
              aria-label={t('admin.assignments.audience.additionalFilters.increaseDays')}
              onClick={() => handleDaysChange(days + 1)}
            />
          </HStack>
        </Flex>
      </Collapse>
    </Stack>
  );
}
